import { Link } from "react-router-dom";
import { Badge } from "../ui/Badge";
import type { ReviewTrustAlert } from "../../../../shared/types";
import { reviewTrustExplanation, reviewTrustOperatorActionText } from "../../../../shared/review-source-risk";

interface Props {
  alerts: ReviewTrustAlert[] | null | undefined;
  mode?: "blocking" | "informational";
}

function alertBannerCopy(
  alerts: ReviewTrustAlert[],
  mode: "blocking" | "informational",
): { badge: string; body: string } {
  const hasSourceRisk = alerts.some((alert) => alert.kind === "source" || alert.kind === "run");
  const hasClaimRisk = alerts.some((alert) => alert.kind === "synthesis");

  if (hasSourceRisk) {
    return {
      badge: mode === "blocking" ? "Source risk" : "Grounding notes",
      body: mode === "blocking"
        ? "Check the supporting evidence before copying or publishing."
        : "Review the supporting notes before publishing.",
    };
  }

  if (hasClaimRisk) {
    return {
      badge: "Claim risk",
      body: "Rewrite unsupported claims before copying or posting.",
    };
  }

  return {
    badge: mode === "blocking" ? "Review alerts" : "Review notes",
    body: "Review these notes before treating the draft as publish-ready.",
  };
}

export function BriefProvenanceAlerts({ alerts, mode = "blocking" }: Props) {
  if (!alerts || alerts.length === 0) return null;
  const copy = alertBannerCopy(alerts, mode);

  return (
    <div className="rounded-md border border-amber-200 bg-amber-50 p-3">
      <div className="flex items-center gap-2">
        <Badge variant="warning">{copy.badge}</Badge>
        <p className="text-sm font-medium text-amber-900">
          {copy.body}
        </p>
      </div>
      <div className="mt-2 space-y-2">
        {alerts.map((alert) => (
          <div key={alert.id} className="text-sm text-amber-900">
            <p>{reviewTrustExplanation(alert)}</p>
            <p className="mt-1 text-xs text-amber-800">{reviewTrustOperatorActionText(alert)}</p>
            {alert.run_id ? (
              <Link to={`/runs/${alert.run_id}`} className="text-xs font-medium text-amber-800 underline underline-offset-2">
                Open source run
              </Link>
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );
}
